// utils/api/optimized-api.ts
import { ApiResponse, Method, RequestConfig } from "./ApiProps";
import Cookies from 'js-cookie';

// Enhanced client-side token helper with caching
function getClientAuthToken(): string | null {
  if (typeof window === 'undefined') return null;
  
  // Check if token is already cached in memory (for this session)
  const cachedToken = (globalThis as any).__CRM_TOKEN_CACHE__;
  if (cachedToken && cachedToken.expiry > Date.now()) {
    return cachedToken.token;
  }

  try {
    const cookieToken = Cookies.get('crm_auth_token');

    if (cookieToken) {
      (globalThis as any).__CRM_TOKEN_CACHE__ = {
        token: cookieToken,
        expiry: Date.now() + 30 * 60 * 1000
      };
      return cookieToken;
    }

    return null;
  } catch (error) {
    return null;
  }
}

// Request interceptor for logging and metrics
interface RequestMetrics {
  startTime: number;
  endTime?: number;
  duration?: number;
  url: string;
  method: string;
  status?: number;
  success: boolean;
}

class OptimizedApi {
  private baseUrl: string;
  private requestMetrics: RequestMetrics[] = [];
  public Method = {
    GET: "GET" as Method,
    POST: "POST" as Method,
    PUT: "PUT" as Method,
    DELETE: "DELETE" as Method,
    PATCH: "PATCH" as Method,
  } as const;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_URL || "http://localhost:8000/api";
    
    if (!this.baseUrl) {
      throw new Error("API base URL is not configured");
    }
  }

  private async getHeaders(customHeaders: Record<string, string> = {}): Promise<HeadersInit> {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...customHeaders
    };

    try {
      if (typeof window !== 'undefined') {
        const token = getClientAuthToken();
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }
      }
    } catch {
      // ignore token errors
    }

    return headers;
  }

  // Enhanced request method with retry logic and caching
  async req<T>({
    url,
    method = "GET",
    body,
    headers = {},
    cache = "no-store",
    retries = 1
  }: RequestConfig & { 
    cache?: RequestCache, 
    retries?: number 
  }): Promise<ApiResponse<T>> {
    
    const metrics: RequestMetrics = {
      startTime: Date.now(),
      url,
      method,
      success: false
    };

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const allHeaders = await this.getHeaders(headers);
        const fullUrl = `${this.baseUrl}${url}`;

        const requestConfig: RequestInit = {
          method,
          headers: allHeaders,
          credentials: 'include',
          cache,
        };

        if (body) {
          requestConfig.body = JSON.stringify(body);
        }

        const response = await fetch(fullUrl, requestConfig);
        
        metrics.status = response.status;
        metrics.endTime = Date.now();
        metrics.duration = metrics.endTime - metrics.startTime;

        if (!response.ok) {
          // Handle different error status codes
          if (response.status === 401) {
            // Clear cached token and auth data on 401
            if (typeof window !== 'undefined') {
              delete (globalThis as any).__CRM_TOKEN_CACHE__;
              document.cookie = 'crm_auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
              localStorage.removeItem('crm_user');
              sessionStorage.clear();
              // Redirect to login using environment variable
              const logoutRedirectUrl = import.meta.env.VITE_LOGOUT_REDIRECT_URL || '/login';
              window.location.href = logoutRedirectUrl;
            }
            throw new Error("Authentication failed. Please log in again.");
          }
          
          if (response.status >= 500 && attempt < retries) {
            await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
            continue;
          }

          let errorData;
          try {
            errorData = await response.json();
          } catch {
            throw new Error(`HTTP error! status: ${response.status}, ${response.statusText}`);
          }

          throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        metrics.success = true;
        this.requestMetrics.push(metrics);

        return data as ApiResponse<T>;

      } catch (error) {
        if (attempt === retries) {
          metrics.endTime = Date.now();
          metrics.duration = metrics.endTime - metrics.startTime;
          this.requestMetrics.push(metrics);

          if (error instanceof Error) {
            throw error;
          }
          throw new Error("An unexpected error occurred during the API request");
        }
      }
    }
    
    throw new Error("Max retries exceeded");
  }

  // Enhanced helper methods with caching options
  async get<T>(
    url: string, 
    params?: Record<string, any>,
    options: { 
      headers?: Record<string, string>,
      cache?: RequestCache 
    } = {}
  ): Promise<ApiResponse<T>> {
    let finalUrl = url;
    
    // Convert params object to query string if provided
    if (params && Object.keys(params).length > 0) {
      const searchParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          searchParams.append(key, String(value));
        }
      });
      
      const queryString = searchParams.toString();
      if (queryString) {
        finalUrl += (url.includes('?') ? '&' : '?') + queryString;
      }
    }
    
    return this.req<T>({ 
      url: finalUrl, 
      method: this.Method.GET, 
      headers: options.headers,
      cache: options.cache 
    });
  }

  async post<T>(
    url: string,
    body: unknown,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.req<T>({ url, method: this.Method.POST, body, headers, retries: 0 });
  }

  async put<T>(
    url: string,
    body: unknown,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.req<T>({ url, method: this.Method.PUT, body, headers, retries: 0 });
  }

  async delete<T>(
    url: string,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.req<T>({ url, method: this.Method.DELETE, headers, retries: 0 });
  }

  async patch<T>(
    url: string,
    body: unknown,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.req<T>({ url, method: this.Method.PATCH, body, headers, retries: 0 });
  }

  // Performance monitoring
  getMetrics(): RequestMetrics[] {
    return this.requestMetrics;
  }

  getAverageResponseTime(): number {
    const validMetrics = this.requestMetrics.filter(m => m.duration);
    if (validMetrics.length === 0) return 0;
    return validMetrics.reduce((sum, m) => sum + (m.duration || 0), 0) / validMetrics.length;
  }

  getErrorRate(): number {
    if (this.requestMetrics.length === 0) return 0;
    const errors = this.requestMetrics.filter(m => !m.success);
    return (errors.length / this.requestMetrics.length) * 100;
  }
}

export const optimizedApi = new OptimizedApi();
export default optimizedApi;